/**
 * The chat panel (online only), bottom-left: the message log — the last lines fade after a few
 * seconds, the whole scrollback while the input is open — and, on Enter, the input with its
 * channel chip (`[ALL]` `[ALLY]` `[→ KESTREL]`: click or ↑ / ↓ to change, or `/a` `/t` `/w NAME`
 * `/r`). Esc closes. While the input is open a click on the map attaches that point (`LOC F7`);
 * Alt+click on the map opens the input with it. A line with a point shows a `[F7]` button that
 * flies the camera there and drops a ping on the map (a DOM overlay placed through the camera,
 * not the renderer). An incoming private line — and an ally's when the log has faded or it carries
 * a point — also comes as a `chat` toast (with the point as a `[F7] LOOK` button). In the room lobby the same log
 * and input sit under the slots, and on the results of a private room's match under the standings
 * (ALL only: the room's chat). `/mute NAME` hides a player's lines on this client for the room.
 *
 * Everything a player wrote is put on screen as text (`textContent`), never as HTML.
 */
import { CHAT_MAX, type ChatErrorMessage, type ChatLoc, type ChatMessage, type ChatSend } from '@sa/protocol';
import type { PlayerId, PlayerView, Tick } from '@sa/sim';
import type { Sfx } from '../audio/sfx';
import type { Camera } from '../input/camera';
import { drawPatternSwatch, type Palette } from '../render/palette';
import {
  ALL,
  ChatLog,
  cycleTarget,
  gridRef,
  lineAlpha,
  parseChatInput,
  previewTarget,
  sameTarget,
  targetLabel,
  validTarget,
  type ChatContext,
  type ChatLine,
  type ChatTarget,
} from '../state/chat';
import type { ToastSpec } from '../state/toasts';
import { cssRgb } from '../theme';
import { h } from './dom';

/** What the panel needs from the match on screen (GameSession.chatHost). */
export interface ChatMatchHost {
  el: HTMLElement;
  canvas: HTMLCanvasElement;
  cam: Camera;
  world: { width: number; height: number };
  view(): PlayerView;
  /** allies that bind us now (none for a spectator) */
  allies(): readonly PlayerId[];
  /** the ally of when one of us fell (FFA; the store keeps it from the moment it happens), 0: none */
  fallenAlly(): PlayerId;
  /** nations knocked out (and when) */
  out(): ReadonlyMap<PlayerId, Tick>;
  pal: Palette;
  nameOf(p: PlayerId): string;
  toast(spec: ToastSpec): void;
  /** the Esc menu is up: Enter does not open the chat */
  modal(): boolean;
}

/** a ping on the map stays this long (ms) */
const PING_MS = 4_500;
/** the log re-checks its fading this often while closed (ms) */
const FADE_TICK_MS = 250;

interface Ping {
  loc: ChatLoc;
  label: string;
  colour: string;
  born: number;
  el: HTMLElement;
}

export class ChatPanel {
  readonly el = h('div.chat');
  private readonly logEl = h('div.chat-log');
  private readonly bar = h('div.chat-in');
  private readonly chip = h('button.chat-chip', { type: 'button', title: 'CHANNEL · CLICK OR ↑↓' });
  private readonly input = h('input.chat-text', { type: 'text', maxLength: CHAT_MAX, autocomplete: 'off' });
  private readonly locChip = h('button.chat-locchip', { type: 'button', title: 'REMOVE THE MAP POINT' });
  private readonly hint = h('div.chat-hint');
  /** the channel we were on closed (`ALLY LINE CLOSED → ALL`): said in the log, the chip amber until we pick or send */
  private closedNote = '';
  /** after a channel closed, the first line to ALL needs a second Enter (`confirmArmed`) */
  private confirmAll = false;
  private confirmArmed = false;
  private readonly pingLayer = h('div.chat-pings');
  private readonly log = new ChatLog();
  private target: ChatTarget = ALL;
  private loc: ChatLoc | null = null;
  private isOpen = false;
  private mode: 'none' | 'lobby' | 'match' = 'none';
  private host: (() => ChatMatchHost | null) | null = null;
  private myName: () => string = () => '';
  private pings: Ping[] = [];
  private fadeTimer: ReturnType<typeof setInterval> | null = null;
  private raf = 0;
  private lastRender = '';
  /** players muted on this client (upper-case names), for the room */
  private readonly muted = new Set<string>();

  constructor(
    private readonly send: (m: ChatSend) => boolean,
    private readonly sfx: Sfx,
  ) {
    this.bar.append(this.chip, this.input);
    // the attached point sits on its own line over the input (the input keeps its width)
    this.el.append(this.logEl, this.locChip, this.bar, this.hint);
    this.locChip.hidden = true;
    // the chip previews where a typed command would send the line (`/w al…` → ALEX)
    this.input.addEventListener('input', () => {
      this.confirmArmed = false;
      this.renderChip();
      this.renderHint();
    });
    this.input.spellcheck = false;
    this.input.placeholder = 'SAY IT';
    this.chip.addEventListener('click', () => this.cycle(1));
    this.locChip.addEventListener('click', () => this.setLoc(null));
    this.input.addEventListener('keydown', (e) => this.onInputKey(e));
    this.el.addEventListener('pointerdown', (e) => e.stopPropagation());
    window.addEventListener('keydown', this.onWindowKey);
  }

  // ------------------------------------------------------------------ mounting

  /** In the room lobby (or, `results`, on a match's results): log and input always up, ALL only. */
  mountLobby(container: HTMLElement, myName: () => string, results = false): void {
    this.unmount();
    this.mode = 'lobby';
    this.myName = myName;
    this.el.classList.add('in-lobby');
    this.el.classList.toggle('in-results', results);
    container.append(this.el);
    this.open();
  }

  /** In an online match (the host appears once the match has loaded). */
  mountMatch(host: () => ChatMatchHost | null): void {
    this.unmount();
    this.mode = 'match';
    this.host = host;
    this.el.classList.remove('in-lobby', 'in-results');
    this.close();
    const attach = () => {
      if (this.mode !== 'match' || this.host !== host) return;
      const hst = host();
      if (!hst) {
        setTimeout(attach, 300);
        return;
      }
      hst.el.append(this.el, this.pingLayer);
      hst.el.addEventListener('pointerdown', this.onMapDown, true);
      this.render();
    };
    attach();
    this.fadeTimer = setInterval(() => this.render(), FADE_TICK_MS);
  }

  unmount(): void {
    const hst = this.mode === 'match' ? this.host?.() : null;
    if (hst) {
      hst.el.removeEventListener('pointerdown', this.onMapDown, true);
    }
    if (this.fadeTimer) clearInterval(this.fadeTimer);
    this.fadeTimer = null;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    for (const p of this.pings) p.el.remove();
    this.pings = [];
    this.el.remove();
    this.pingLayer.remove();
    this.mode = 'none';
    this.host = null;
    this.isOpen = false;
    this.setLoc(null);
  }

  /** A new room: nothing said before carries over. */
  reset(): void {
    this.log.clear();
    this.muted.clear();
    this.target = ALL;
    this.closedNote = '';
    this.confirmAll = false;
    this.lastRender = '';
    this.render();
  }

  // ------------------------------------------------------------------ incoming

  receive(m: ChatMessage | ChatErrorMessage): void {
    const now = performance.now();
    if (m.type === 'chat.error') {
      this.log.system(m.msg, now);
      this.sfx.error();
      this.render();
      return;
    }
    const mine = this.mode === 'match' ? m.from === this.ctx().me : m.name === this.myName();
    // a muted player: kept out of sight (and of the log, so unmuting does not replay them)
    if (!mine && this.muted.has(m.name.toUpperCase())) return;
    const faded = !this.isOpen && this.log.visible(now, false).length === 0;
    const line = this.log.add(m, mine, now);
    if (!mine) {
      const hst = this.mode === 'match' ? this.host?.() : null;
      const name = m.name.toUpperCase();
      // the point rides on the card as a button (the text stays whole)
      const buttons = m.loc && hst ? [{ key: null, label: `[${gridRef(m.loc.x, m.loc.y, hst.world.width, hst.world.height)}] LOOK`, run: () => this.goTo(line) }] : undefined;
      const card = { kind: 'chat' as const, text: m.text, tone: 'ally' as const, from: m.from, key: `chat:${line.id}`, ...(buttons ? { buttons } : {}) };
      if (hst && m.channel === 'pm') hst.toast({ ...card, title: `CHAT · PRIVATE · ${name}` });
      // an ally's line comes as a card too when the log has faded — or when it points somewhere (intel)
      else if (hst && m.channel === 'ally' && (faded || m.loc)) hst.toast({ ...card, title: `CHAT · ALLY · ${name}` });
      // a toast sounds on its own; the rest gets the chat blip
      else this.sfx.toast(m.channel === 'all' ? 'info' : 'ally', 'chat');
    }
    this.render();
  }

  // ------------------------------------------------------------------ input

  private onWindowKey = (e: KeyboardEvent): void => {
    if (this.mode !== 'match' || this.isOpen || e.key !== 'Enter' || e.repeat) return;
    const t = e.target as HTMLElement | null;
    if (t && (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement || t.isContentEditable)) return;
    if (this.host?.()?.modal()) return;
    e.preventDefault();
    this.open();
  };

  private onInputKey(e: KeyboardEvent): void {
    // the game's hotkeys never see a key typed here
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      this.submit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (this.mode === 'match') this.close();
      else this.input.value = '';
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      this.cycle(e.key === 'ArrowUp' ? -1 : 1);
    }
  }

  private open(loc: ChatLoc | null = null): void {
    this.isOpen = true;
    this.el.classList.add('open');
    if (loc) this.setLoc(loc);
    this.checkChannel();
    this.render(true);
    this.input.focus();
  }

  private close(): void {
    this.isOpen = false;
    this.confirmArmed = false;
    this.el.classList.remove('open');
    this.input.value = '';
    this.setLoc(null);
    this.input.blur();
    this.render(true);
  }

  private cycle(dir: 1 | -1): void {
    if (this.mode === 'lobby') return;
    this.target = cycleTarget(this.target, this.ctx(), dir);
    // a channel picked by hand: the closed one is old news
    this.clearClosed();
    this.render(true);
    this.input.focus();
  }

  private submit(): void {
    const ctx = this.ctx();
    const r = parseChatInput(this.input.value, this.target, ctx);
    const now = performance.now();
    switch (r.kind) {
      case 'none':
        if (this.mode === 'match') this.close();
        return;
      case 'error':
        this.log.system(r.msg, now);
        this.sfx.error();
        this.render(true);
        return;
      case 'switch':
        this.target = r.target;
        this.input.value = '';
        this.render(true);
        return;
      case 'mute': {
        if (r.name !== null) {
          if (r.on) this.muted.add(r.name);
          else this.muted.delete(r.name);
        }
        const list = [...this.muted].join(' · ');
        this.log.system(r.name === null ? (list ? `MUTED: ${list}` : 'NOBODY MUTED · /MUTE NAME') : `${r.name} ${r.on ? 'MUTED · /UNMUTE' : 'UNMUTED'} ${r.on ? r.name : ''}`.trim(), now);
        this.input.value = '';
        this.render(true);
        return;
      }
      case 'send': {
        // our channel closed under us (a betrayal, a player gone): the first line to ALL asks twice
        if (this.confirmAll && r.target.channel === 'all' && !this.confirmArmed) {
          this.confirmArmed = true;
          this.sfx.error();
          this.renderHint();
          return;
        }
        const m: ChatSend = { type: 'chat', channel: r.target.channel, text: r.text, ...(r.target.channel === 'pm' ? { to: r.target.to } : {}), ...(this.loc ? { loc: this.loc } : {}) };
        if (!this.send(m)) {
          this.log.system('NO LINK · NOT SENT', now);
          this.sfx.error();
          this.render(true);
          return;
        }
        // a private line keeps its channel (a conversation); the others too — the chip says where
        this.target = r.target;
        this.clearClosed();
        this.sfx.click();
        if (this.loc) this.dropPing(this.loc, 'LOC', 'var(--me)');
        if (this.mode === 'match') this.close();
        else {
          this.input.value = '';
          this.render(true);
        }
      }
    }
  }

  private setLoc(loc: ChatLoc | null): void {
    this.loc = loc;
    const hst = this.mode === 'match' ? this.host?.() : null;
    this.locChip.hidden = !loc || !hst;
    if (loc && hst) this.locChip.textContent = `LOC ${gridRef(loc.x, loc.y, hst.world.width, hst.world.height)} ×`;
    this.renderHint();
    if (this.isOpen) this.input.focus();
  }

  /** The map point under a client position (null: off the map). */
  private mapPoint(cx: number, cy: number): ChatLoc | null {
    const hst = this.host?.();
    if (!hst) return null;
    const r = hst.canvas.getBoundingClientRect();
    const [x, y] = hst.cam.screenToWorld(cx - r.left, cy - r.top);
    if (x < 0 || y < 0 || x > hst.world.width || y > hst.world.height) return null;
    return { x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 };
  }

  /** Map clicks while typing attach the point (the game does not see them); Alt+click opens the chat with it. */
  private onMapDown = (e: PointerEvent): void => {
    const hst = this.host?.();
    if (!hst || e.target !== hst.canvas || e.button !== 0) return;
    if (!this.isOpen && !e.altKey) return;
    const at = this.mapPoint(e.clientX, e.clientY);
    if (!at) return;
    e.stopPropagation();
    e.preventDefault();
    if (this.isOpen) this.setLoc(at);
    else this.open(at);
    this.sfx.click();
  };

  // ------------------------------------------------------------------ the match

  /**
   * The channel we are on closed (an ally broke with us, a private partner left): back to ALL —
   * said when it happens (a line in the log, a note over the input), and the first line to ALL
   * after it needs a second Enter.
   */
  private checkChannel(): void {
    if (this.mode !== 'match' || !this.host?.()) return;
    const ctx = this.ctx();
    if (sameTarget(validTarget(this.target, ctx), this.target)) return;
    const was = this.target.channel === 'ally' ? 'ALLY LINE' : `${targetLabel(this.target, (p) => this.nameOf(p)).replace('→ ', '')} LINE`;
    this.target = ALL;
    this.closedNote = `${was} CLOSED → ALL`;
    this.confirmAll = true;
    this.confirmArmed = false;
    this.log.system(this.closedNote, performance.now());
    this.render(true);
  }

  private clearClosed(): void {
    this.closedNote = '';
    this.confirmAll = false;
    this.confirmArmed = false;
  }

  /** Who we can talk to, from the match view (lobby: ALL only). */
  private ctx(): ChatContext {
    const hst = this.mode === 'match' ? this.host?.() : null;
    if (!hst) return { me: 0, peers: [], hasAlly: false, lastPm: null, lobby: true };
    const v = hst.view();
    const allies = hst.allies();
    const myTeam = v.players.find((p) => p.id === v.me)?.team;
    const teammates = v.mode === 'teams' && v.players.some((p) => p.id !== v.me && p.team === myTeam);
    return {
      me: v.me,
      peers: v.players.map((p) => ({ id: p.id, name: p.name.toUpperCase(), isBot: p.isBot })),
      // (an alliance that ended with a fall, ours or theirs, leaves the two an ALLY line)
      hasAlly: teammates || allies.length > 0 || hst.fallenAlly() > 0,
      lastPm: this.log.lastPm,
      lobby: false,
    };
  }

  private nameOf(p: PlayerId): string {
    return (this.host?.()?.nameOf(p) ?? `P${p}`).toUpperCase();
  }

  private dropPing(loc: ChatLoc, label: string, colour: string): void {
    const hst = this.host?.();
    if (!hst) return;
    const el = h('div.chat-ping', { style: { color: colour } }, h('i'), h('i'), h('b'), h('span', {}, label));
    this.pingLayer.append(el);
    this.pings.push({ loc, label, colour, born: performance.now(), el });
    if (!this.raf) this.raf = requestAnimationFrame(this.placePings);
  }

  private placePings = (): void => {
    const hst = this.host?.();
    const now = performance.now();
    this.pings = this.pings.filter((p) => {
      if (!hst || now - p.born > PING_MS) {
        p.el.remove();
        return false;
      }
      const [sx, sy] = hst.cam.worldToScreen(p.loc.x, p.loc.y);
      p.el.style.transform = `translate(${sx.toFixed(1)}px, ${sy.toFixed(1)}px)`;
      p.el.style.opacity = String(Math.min(1, (PING_MS - (now - p.born)) / 800));
      return true;
    });
    this.raf = this.pings.length ? requestAnimationFrame(this.placePings) : 0;
  };

  private goTo(line: ChatLine): void {
    const hst = this.host?.();
    if (!hst || !line.loc) return;
    hst.cam.flyTo(line.loc.x, line.loc.y);
    const colour = line.from ? cssRgb(hst.pal.color(line.from)) : 'var(--me)';
    this.dropPing(line.loc, line.kind === 'mine' ? 'LOC' : line.name.toUpperCase(), colour);
    this.sfx.click();
  }

  // ------------------------------------------------------------------ drawing

  private render(force = false): void {
    if (this.mode === 'none') return;
    // (checked as it happens, the input open or not: the log says so at once)
    if (this.mode === 'match' && !force) this.checkChannel();
    const now = performance.now();
    const lines = this.log.visible(now, this.isOpen || this.mode === 'lobby').filter((l) => l.kind !== 'theirs' || !this.muted.has(l.name.toUpperCase()));
    // closed: only the fading changes between renders
    const key = `${this.isOpen}|${this.target.channel}|${'to' in this.target ? this.target.to : ''}|${lines.map((l) => l.id).join(',')}`;
    if (force || key !== this.lastRender) {
      this.lastRender = key;
      const hst = this.mode === 'match' ? this.host?.() : null;
      this.logEl.replaceChildren(...lines.map((l) => this.lineEl(l, hst)));
      this.renderChip();
      this.renderHint();
      if (this.isOpen || this.mode === 'lobby') this.logEl.scrollTop = this.logEl.scrollHeight;
    }
    if (!this.isOpen && this.mode === 'match') {
      const kids = this.logEl.children;
      lines.forEach((l, i) => ((kids[i] as HTMLElement).style.opacity = String(lineAlpha(l, now))));
    }
    this.el.classList.toggle('quiet', !this.isOpen && this.mode === 'match' && lines.length === 0);
    // (the results: no empty log box until someone says something — the input stays)
    this.el.classList.toggle('empty', lines.length === 0);
  }

  /** The channel chip: where Enter sends the line — a typed command previewed live, a bad one in red. */
  private renderChip(): void {
    const p = this.isOpen && this.mode === 'match' ? previewTarget(this.input.value, this.target, this.ctx()) : { target: this.target };
    if ('error' in p) {
      this.chip.textContent = `[${p.error}]`;
      this.chip.className = 'chat-chip bad';
      return;
    }
    this.chip.textContent = `[${targetLabel(p.target, (q) => this.nameOf(q))}]`;
    // (amber while our channel just closed under us: this is ALL now)
    this.chip.className = `chat-chip ch-${p.target.channel}${sameTarget(p.target, this.target) ? '' : ' preview'}${this.closedNote && sameTarget(p.target, this.target) ? ' reverted' : ''}`;
  }

  private renderHint(): void {
    this.hint.classList.toggle('warn', this.confirmArmed);
    this.hint.textContent =
      this.mode === 'lobby'
        ? 'ENTER SEND'
        : this.confirmArmed
          ? 'TO EVERYONE? ENTER AGAIN TO SEND TO ALL · ↑↓ CHANNEL'
          : this.loc
            ? 'ENTER SEND · ESC · CLICK MAP: MOVE THE POINT'
            : 'ENTER SEND · ESC · ↑↓ CHANNEL · /? HELP';
  }

  private lineEl(l: ChatLine, hst: ChatMatchHost | null | undefined): HTMLElement {
    if (l.kind === 'sys') return h('div.cl.sys', {}, h('span.cl-text', {}, l.text));
    const tag = l.channel === 'all' ? 'ALL' : l.channel === 'ally' ? 'ALLY' : l.kind === 'mine' ? `PM → ${(l.toName ?? '').toUpperCase()}` : 'PM';
    const colour = hst && l.from ? cssRgb(hst.pal.color(l.from)) : l.kind === 'mine' ? 'var(--me)' : 'var(--white)';
    let sw: HTMLCanvasElement | null = null;
    if (hst && l.from) {
      sw = h('canvas.sw', { width: 8, height: 8 });
      drawPatternSwatch(sw.getContext('2d')!, hst.pal.pattern(l.from), hst.pal.color(l.from), 8, 8);
    }
    const loc =
      l.loc && hst
        ? h('button.cl-loc', { type: 'button', title: 'LOOK THERE', onclick: () => this.goTo(l) }, `[${gridRef(l.loc.x, l.loc.y, hst.world.width, hst.world.height)}]`)
        : null;
    return h(
      `div.cl.ch-${l.channel}${l.kind === 'mine' ? '.mine' : ''}`,
      {},
      h('span.cl-tag', {}, `[${tag}]`),
      sw,
      h('span.cl-name', { style: { color: colour } }, l.name.toUpperCase()),
      h('span.cl-text', {}, l.text),
      loc,
    );
  }
}
